import { useMemo, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import clsx from "clsx";
import { ChevronLeft, ChevronRight, Lock, Luggage, Moon, Plane, Truck, Wrench, X } from "lucide-react";
import { dayWindow, icaoCodeForStand, type AirportConfig, type StandAssignment, type StandClosure, type Turn } from "@yb/core";
import type { Board } from "../lib/api";
import { addDays, airlineColor, delayMin, delayText, hhmm, label, readableOn } from "../lib/format";
import { checkDrop, type DropCheck } from "../lib/validate";
import { extendSpan, extensionText } from "../lib/extend";
import { GridLines, LABEL_W, NowLine, TimeHeader, boardWindow, makeScale, packLanes, useAutoScroll } from "./timeline-shared";
import { cameraTurnFor, indexCameraTurns, isLive, type CameraTurn } from "../lib/apron";
import { CameraChip, StandCameraChip } from "./Cameras";

const ROW_H = 46;

export interface TimelinePreview {
  stands: Record<string, StandAssignment>;
  moved: Set<string>;
  closures?: StandClosure[];
  turns?: Turn[];
}

interface Props {
  board: Board;
  config: AirportConfig;
  now: number;
  ppm: number;
  selected: string | null;
  onSelect: (turnId: string | null) => void;
  onDrop: (turnId: string, standId: string | null) => void;
  onReject: (reason: string) => void;
  onRemoveClosure: (id: string) => void;
  onCloseStand: (standId: string) => void;
  /** Open the day before or after, scrolled to `focusAt`: the board runs on into the next day. */
  onDay?: (date: string, focusAt: number) => void;
  onHover: (turnId: string | null, x: number, y: number) => void;
  onContext: (turnId: string, x: number, y: number) => void;
  /**
   * The right edge of a bar dragged to a new end: hold the aircraft on its stand until
   * then. Dragged back to the usual end, the extension comes off.
   */
  onExtend?: (turnId: string, until: number) => void;
  canEdit: boolean;
  preview: TimelinePreview | null;
  focusAt: number | null;
  /** How the reader wants bars painted. */
  look: BarLook;
  /** What Vectro Apron's cameras saw on the day, when the feed is on. */
  cameras?: CameraTurn[];
}

/** Airline colour, status colour, or the original panel-with-an-edge. */
export type BarLook = "airline" | "status" | "classic";

/** Stands & gates board. */
export function Timeline(props: Props) {
  const { board, config, now, ppm, selected, onSelect, canEdit, preview, look } = props;
  const tz = board.airport.timezone;
  const { from, to } = boardWindow(board.date, tz);
  const scale = makeScale(from, to, ppm);
  const { x, width } = scale;
  // Flights already gone are faded on today's board, to bring forward what is still to come.
  // A past day is all gone, and faded whole it could not be read.
  const fadeDone = now < dayWindow(board.date, tz).to;
  const scroller = useRef<HTMLDivElement>(null);
  const [atEdge, setAtEdge] = useState({ start: false, end: false });
  const [drag, setDrag] = useState<{ turnId: string; standId: string | null; check: DropCheck | null; y: number; x: number } | null>(null);
  const [resize, setResize] = useState<{ turnId: string; startX: number; end: number; usual: number; until: number; x: number; y: number } | null>(null);
  useAutoScroll(scroller, scale, now, `${board.date}|${ppm}`, props.focusAt);

  const turns = preview?.turns ?? board.turns;
  const stands = preview?.stands ?? board.plan.stands;
  const closures = preview?.closures ?? board.closures;
  const activeStands = config.stands.filter((s) => s.active).sort((a, b) => a.order - b.order);

  const conflictTurns = useMemo(() => {
    const m = new Map<string, "hard" | "warning">();
    if (preview) return m;
    for (const c of board.conflicts) for (const id of c.turnIds) if (m.get(id) !== "hard") m.set(id, c.severity);
    return m;
  }, [board.conflicts, preview]);

  const predictedGrowth = useMemo(() => {
    const m = new Map<string, { min: number; p: number }>();
    if (preview) return m;
    for (const p of board.predictions) m.set(p.causeTurnId, { min: p.causeDelayMin, p: p.probability });
    return m;
  }, [board.predictions, preview]);

  const byStand = useMemo(() => {
    const m = new Map<string | null, Turn[]>();
    for (const t of turns) {
      const sid = stands[t.id]?.standId ?? null;
      m.set(sid, [...(m.get(sid) ?? []), t]);
    }
    return m;
  }, [turns, stands]);

  /** Last night and tomorrow morning, from their own days' plans: context, not this plan. */
  const edgeByStand = useMemo(() => {
    const m = new Map<string, Turn[]>();
    const shown = new Set(turns.map((t) => t.id));
    for (const t of board.edges?.turns ?? []) {
      if (shown.has(t.id)) continue;
      const sid = board.edges?.stands[t.id]?.standId;
      if (!sid) continue;
      m.set(sid, [...(m.get(sid) ?? []), t]);
    }
    return m;
  }, [board.edges, turns]);

  /**
   * The cameras' word on each bar, for turns they see now (anything but an empty stand),
   * and per stand the live camera turn that no bar on that stand row accounts for: an
   * aircraft the cameras see where the plan has nothing, or has something else.
   */
  const camera = useMemo(() => {
    const byTurn = new Map<string, CameraTurn>();
    const unplaced = new Map<string, CameraTurn>();
    const list = props.cameras ?? [];
    if (!list.length || preview) return { byTurn, unplaced };
    const index = indexCameraTurns(list, tz);
    const claimed = new Set<string>();
    for (const t of turns) {
      const c = cameraTurnFor(t, index, tz);
      if (!c || !isLive(c)) continue;
      byTurn.set(t.id, c);
      if (c.standId && stands[t.id]?.standId === c.standId) claimed.add(c.turnId);
    }
    // This plan follows its own rules, so an aircraft it has elsewhere is expected; only flagged if asked for.
    if (config.settings.apronShowPlanDifferences === true) for (const c of list) if (isLive(c) && c.standId && !claimed.has(c.turnId)) unplaced.set(c.standId, c);
    return { byTurn, unplaced };
  }, [props.cameras, preview, turns, stands, tz, config.settings.apronShowPlanDifferences]);

  const previousStand = useMemo(() => {
    if (!preview) return new Map<string, string | null>();
    return new Map(board.turns.map((t) => [t.id, board.plan.stands[t.id]?.standId ?? null]));
  }, [preview, board]);

  // ---------------------------------------------------------------- drag
  const onPointerDown = (e: RPointerEvent, t: Turn) => {
    if (!canEdit || preview || e.button !== 0 || t.segment === "towed") return;
    if (t.dep?.actual != null && t.end < now) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    props.onHover(null, 0, 0);
    setDrag({ turnId: t.id, standId: stands[t.id]?.standId ?? null, check: null, y: e.clientY, x: e.clientX });
  };
  /** Grabbing a bar's right edge stretches it instead of moving it. */
  const onResizeDown = (e: RPointerEvent, t: Turn, usual: number) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    props.onHover(null, 0, 0);
    setResize({ turnId: t.id, startX: e.clientX, end: t.end, usual, until: t.end, x: e.clientX, y: e.clientY });
  };
  const onPointerMove = (e: RPointerEvent) => {
    if (resize) {
      // Five-minute steps, and never shorter than the stay the flights themselves need.
      const step = 5 * 60_000;
      const moved = Math.round(((e.clientX - resize.startX) / ppm) * 60_000 / step) * step;
      setResize({ ...resize, until: Math.max(resize.usual, resize.end + moved), x: e.clientX, y: e.clientY });
      return;
    }
    if (!drag) return;
    const el = document.elementsFromPoint(e.clientX, e.clientY).find((n) => (n as HTMLElement).dataset?.stand !== undefined) as
      | HTMLElement
      | undefined;
    const standId = el ? (el.dataset.stand === "" ? null : el.dataset.stand!) : drag.standId;
    const t = turns.find((x) => x.id === drag.turnId)!;
    const check = standId ? checkDrop(t, standId, turns, stands, config, closures) : { level: "soft" as const, reason: "Leave unallocated" };
    setDrag({ ...drag, standId, check, y: e.clientY, x: e.clientX });
  };
  const onPointerUp = (e: RPointerEvent) => {
    if (resize) {
      if (resize.until !== resize.end) props.onExtend?.(resize.turnId, resize.until);
      setResize(null);
      return;
    }
    if (!drag) return;
    const original = stands[drag.turnId]?.standId ?? null;
    if (drag.check && drag.standId !== original) {
      // Hard rule breaks are refused unless the planner deliberately overrides with Shift.
      if (drag.check.level === "bad" && !e.shiftKey) props.onReject(`${drag.standId}: ${drag.check.reason}. Hold Shift to override.`);
      else props.onDrop(drag.turnId, drag.standId);
    } else {
      onSelect(drag.turnId);
    }
    setDrag(null);
  };

  // ---------------------------------------------------------------- bars
  /** "162 pax · 140 bags", from whichever of the two we have. */
  const loadText = (t: Turn): string | null => {
    const pax = t.dep?.pax ?? t.arr?.pax ?? null;
    const bags = t.dep?.bags ?? t.arr?.bags ?? null;
    const parts = [pax != null ? `${pax} pax` : null, bags != null ? `${bags} bags` : null].filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
  };

  const turnBar = (t: Turn) => {
    // Nothing is drawn beyond the board's own window. A turn that claims to run for days
    // would otherwise paint over the whole day and hide everything under it.
    t = { ...t, start: Math.max(t.start, from), end: Math.min(t.end, to) };
    const left = x(t.start);
    const w = Math.max(x(t.end) - left, 18);

    const c = conflictTurns.get(t.id);
    const grow = predictedGrowth.get(t.id);
    const moved = preview?.moved.has(t.id);
    const locked = stands[t.id]?.locked;
    const done = t.dep ? t.dep.actual != null && t.end < now : t.arr?.actual != null && t.end < now;
    const onBlock = (t.arr?.actual != null || t.segment === "towed") && t.start <= now && t.end >= now;
    const d = delayMin(t.dep ?? t.arr);
    const airline = airlineColor(t.airline, config);
    /**
     * How a bar is painted is a matter of the job in hand, so it is the reader's choice.
     * Airline: the whole bar in the carrier's colour, for reading a row as one airline's
     * day. Status: green, amber, red by what is wrong with it, for hunting problems.
     * Classic: the panel with a coloured edge, which is what this always looked like.
     */
    const color =
      look === "status"
        ? c === "hard"
          ? "#c0392b"
          : c === "warning"
            ? "#b7791f"
            : d !== null && d >= 15
              ? "#8a6d3b"
              : "#2f855a"
        : airline;
    const solid = look !== "classic";
    const ink = readableOn(color);
    const isSel = selected === t.id;
    const load = loadText(t);
    const type = t.aircraftType ? `${t.aircraftType}${t.aircraftTypeInferred ? "*" : ""}` : "";
    // Overnight: shade the part of the stay between the last evening flight and the morning one.
    const mechFrom = t.mech ? Math.max(t.start, Math.min(t.end, t.mech.until - 1)) : null;
    const origEnd = t.dep ? Math.max(t.start, t.dep.estimated ?? t.dep.scheduled) : null;
    // Held on the stand past its usual end by a planner: the added part is striped, and
    // only the last part of a visit can be stretched, since that is where it ends.
    const span = extendSpan(board, "stand", t);
    const ext = t.extensions?.stand;
    const extFrom = ext?.from !== undefined && ext.until > ext.from && ext.from < t.end ? Math.max(t.start, ext.from) : null;
    const stretchable =
      canEdit && !preview && !!props.onExtend && !!span && span.end === t.end && !(t.dep?.actual != null && t.end < now) && t.end <= to;
    const stretching = resize?.turnId === t.id ? resize : null;
    // A turn is two flights. Wide enough, the bar says which is which: the arrival on
    // the left, the departure on the right under a slightly deeper tone, so a glance
    // at the board reads "in as 8280, out as 8281" without opening anything.
    const split = !!t.arr && !!t.dep && w > 150 && t.segment !== "towed";
    const cam = w > 90 ? camera.byTurn.get(t.id) : undefined;
    return (
      <div key={t.id}>
        {grow && grow.min > 0 && (
          <div
            className="absolute top-1.5 bottom-1.5 rounded-r-md border-2 border-dashed border-warn/80 bg-warn/10"
            style={{ left: left + w - 4, width: grow.min * ppm + 4 }}
            title={`Predicted to run ${grow.min} min late (${Math.round(grow.p * 100)}% likely)`}
          />
        )}
        <div
          role="button"
          tabIndex={0}
          onPointerDown={(e) => onPointerDown(e, t)}
          onMouseEnter={(e) => !drag && props.onHover(t.id, e.clientX, e.clientY)}
          onMouseMove={(e) => !drag && props.onHover(t.id, e.clientX, e.clientY)}
          onMouseLeave={() => props.onHover(null, 0, 0)}
          onContextMenu={(e) => {
            e.preventDefault();
            props.onHover(null, 0, 0);
            props.onContext(t.id, e.clientX, e.clientY);
          }}
          onKeyDown={(e) => e.key === "Enter" && onSelect(t.id)}
          onClick={(e) => {
            if (!canEdit || preview) {
              e.stopPropagation();
              onSelect(t.id);
            }
          }}
          className={clsx(
            "absolute top-1.5 bottom-1.5 flex items-stretch overflow-hidden rounded-md border text-left shadow-sm transition-[box-shadow,opacity] select-none",
            canEdit && !preview && t.segment !== "towed" ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
            done && fadeDone && "opacity-50",
            !solid && (t.ron ? "bg-panel-2" : "bg-panel"),
            c === "hard" ? "border-bad ring-2 ring-bad/60" : c === "warning" ? "border-warn ring-1 ring-warn/60" : "border-line",
            (t.arr ?? t.dep)?.source === "projected" && "border-dashed opacity-90",
            moved && "ring-2 ring-brand border-brand flash",
            isSel && "ring-2 ring-info border-info z-20",
            drag?.turnId === t.id && "opacity-40",
            (t.openStart || t.openEnd) && "border-dashed",
          )}
          style={{ left, width: w, ...(solid ? { background: color, color: ink } : {}) }}
        >
          {!solid && <div className="w-1.5 shrink-0" style={{ background: airline }} />}
          {t.segment === "towed" && <Truck className="my-auto ml-1 size-3 shrink-0 opacity-90" aria-label="Towed here" />}
          {t.segment === "departure" && <Truck className="my-auto ml-1 size-3 shrink-0 opacity-80" aria-label="Towed in" />}
          <div
            className={clsx("relative min-w-0 flex-1 px-1.5 py-0.5 leading-tight", (t.openStart || t.openEnd) && "hatch")}
            style={left < 0 ? { paddingLeft: Math.min(-left + 6, Math.max(6, w - 120)) } : undefined}
          >
            {t.ron && <div className="ron-band pointer-events-none absolute inset-0" />}
            {split && <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 bg-black/10" aria-hidden />}
            <div className="relative flex items-center gap-1 truncate text-[12px] font-semibold">
              {onBlock && <Plane className="size-3 shrink-0" aria-label="On stand" />}
              {split ? (
                <>
                  <span className="truncate" title={`Arrives as ${t.arr!.flightNumber}`}>
                    <span className="opacity-75">↓</span> {t.arr!.flightNumber}
                  </span>
                  <span className="flex-1" />
                  <span className="truncate" title={`Departs as ${t.dep!.flightNumber}`}>
                    <span className="opacity-75">↑</span> {t.dep!.flightNumber}
                  </span>
                </>
              ) : (
                <span className="truncate">
                  {t.arr && !t.dep && <span className="opacity-75">↓ </span>}
                  {!t.arr && t.dep && <span className="opacity-75">↑ </span>}
                  {label(t)}
                </span>
              )}
              {d !== null && delayText(d) && (
                <span className="tabular rounded bg-black/25 px-1 text-[10px] font-bold">{delayText(d)}</span>
              )}
              {t.ron && (
                <span className="flex shrink-0 items-center gap-0.5 rounded bg-black/25 px-1 text-[9px] font-bold">
                  <Moon className="size-2.5" /> RON
                </span>
              )}
              {cam && <CameraChip turn={cam} onBar />}
              {t.mech && <Wrench className="size-3 shrink-0" aria-label="Maintenance hold" />}
              {locked && <Lock className="size-3 shrink-0 opacity-80" aria-label="Locked" />}
            </div>
            {w > 70 && (
              <div
                className={clsx("tabular relative truncate text-[10.5px]", solid ? "opacity-90" : "text-ink-2")}
                title={t.deiceMin ? `Holds the stand ${t.deiceMin} min past its departure time for de-icing` : undefined}
              >
                {hhmm(t.start, tz)}–{hhmm(t.end, tz)} {type && <span className="opacity-80">· {type}</span>}
                {t.sector !== "D" && <span className="ml-1 rounded bg-black/25 px-1 text-[9px] font-bold">{t.sector === "T" ? "TB" : "INTL"}</span>}
                {/* Passengers and bags, where a source has given them: the two numbers a
                    duty manager reads a bar for, without having to open anything. */}
                {w > 150 && load && <span className={clsx("ml-1", solid ? "opacity-80" : "text-ink-3")}>· {load}</span>}
              </div>
            )}
          </div>
          {t.segment === "arrival" && <Truck className="my-auto mr-1 size-3 shrink-0 opacity-80" aria-label="Towed off" />}
          {mechFrom !== null && origEnd !== null && t.mech!.until > origEnd && (
            <div
              className="absolute top-0 bottom-0 bg-bad/15"
              style={{ left: Math.max(0, x(origEnd) - left), right: 0 }}
              title={`Maintenance hold until ${hhmm(t.mech!.until, tz)}: ${t.mech!.note}`}
            />
          )}
          {extFrom !== null && (
            <div
              className="extend-tail absolute top-0 bottom-0"
              style={{ left: Math.max(0, x(extFrom) - left), right: 0 }}
              title={extensionText("stand", ext!, ext!.from!, tz)}
              data-extended="stand"
            />
          )}
          {stretchable && (
            <div
              className="absolute inset-y-0 right-0 z-10 w-1.5 cursor-ew-resize hover:bg-black/20"
              onPointerDown={(e) => onResizeDown(e, t, span!.usual)}
              title="Drag to keep the aircraft on the stand longer"
            />
          )}
        </div>
        {stretching && stretching.until !== t.end && (
          <div
            className="pointer-events-none absolute top-1 bottom-1 rounded-md border-2 border-dashed border-warn"
            style={{ left, width: Math.max(x(stretching.until) - left, 18) }}
          />
        )}
        {moved && previousStand.get(t.id) !== undefined && (
          <div className="pointer-events-none absolute -top-0.5 z-10 rounded bg-brand px-1 text-[9px] font-bold text-brand-ink" style={{ left: left + 4 }}>
            from {previousStand.get(t.id) ?? "unallocated"}
          </div>
        )}
      </div>
    );
  };

  const rowTint = (standId: string | null) => {
    if (!drag || drag.standId !== standId || !drag.check) return "";
    return drag.check.level === "ok" ? "bg-ok/12" : drag.check.level === "soft" ? "bg-warn/12" : "bg-bad/12";
  };

  const unallocated = byStand.get(null) ?? [];
  const stretchingTurn = resize ? turns.find((t) => t.id === resize.turnId) : undefined;
  // What the stretch would run into on the same stand, said before the button is let go.
  const stretchClash =
    resize && stretchingTurn
      ? turns.find(
          (u) =>
            u.id !== stretchingTurn.id &&
            stands[u.id]?.standId != null &&
            stands[u.id]?.standId === stands[stretchingTurn.id]?.standId &&
            u.start >= stretchingTurn.start &&
            u.start < resize.until &&
            u.end > resize.end,
        )
      : undefined;
  const cancelledLanes = packLanes(board.cancelled ?? []);

  return (
    <div className="relative h-full">
    <div
      ref={scroller}
      className="scroll-thin relative h-full overflow-auto"
      onScroll={(e) => {
        const el = e.currentTarget;
        const start = el.scrollLeft <= 40;
        const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 40;
        if (start !== atEdge.start || end !== atEdge.end) setAtEdge({ start, end });
      }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClick={(e) => {
        if (e.target === e.currentTarget) onSelect(null);
      }}
    >
      <div style={{ width: width + LABEL_W }} className="relative">
        <TimeHeader scale={scale} now={now} tz={tz} label={`${board.airport.iata} local time`} />
        {activeStands.map((s) => (
          <div key={s.id} className={clsx("flex border-b border-line", rowTint(s.id))} style={{ height: ROW_H }} data-stand={s.id}>
            <div className="group sticky left-0 z-40 flex shrink-0 items-center gap-2 border-r border-line bg-panel px-3" style={{ width: LABEL_W }} data-stand={s.id}>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold">{s.name}</div>
                <div className="flex gap-1 text-[10px] text-ink-3">
                  {s.gate && <span>{s.gate}</span>}
                  {s.jetbridge && <span className="rounded bg-ok/15 px-1 font-semibold text-ok">JB</span>}
                  {s.international && (
                    <span className="rounded bg-info/15 px-1 font-semibold text-info">{s.international === "primary" ? "INTL" : "INTL fb"}</span>
                  )}
                  <span className="rounded bg-ink-3/15 px-1 font-semibold" title={`Takes ICAO Code ${icaoCodeForStand(s.maxWingspan)} and smaller (up to ${s.maxWingspan} m wingspan)`}>
                    {icaoCodeForStand(s.maxWingspan)}
                  </span>
                  <span>{s.maxWingspan}m</span>
                </div>
                {camera.unplaced.get(s.id) && <StandCameraChip cam={camera.unplaced.get(s.id)!} tz={tz} />}
              </div>
              {canEdit && !preview && (
                <button
                  onClick={() => props.onCloseStand(s.id)}
                  className="hidden rounded px-1.5 py-0.5 text-[10px] font-medium text-ink-3 group-hover:block hover:bg-bad/10 hover:text-bad"
                  title={`Close ${s.name}…`}
                >
                  Close…
                </button>
              )}
            </div>
            <div className="relative" style={{ width }} data-stand={s.id}>
              <GridLines scale={scale} />
              {closures
                .filter((c) => c.stand === s.id)
                .map((c) => (
                  <div
                    key={c.id}
                    className="closure-hatch group/cl absolute top-0 bottom-0 flex items-center justify-center border-x border-bad/50 text-[10px] font-semibold text-bad"
                    style={{ left: x(c.from), width: x(c.to) - x(c.from) }}
                    title={`Closed ${hhmm(c.from, tz)}–${hhmm(c.to, tz)}: ${c.reason}`}
                  >
                    <span className="truncate px-1">Closed · {c.reason}</span>
                    {canEdit && !preview && !c.id.startsWith("cl-") && (
                      <button className="ml-1 hidden rounded bg-panel p-0.5 group-hover/cl:block" onClick={() => props.onRemoveClosure(c.id)} title="Reopen stand">
                        <X className="size-3" />
                      </button>
                    )}
                  </div>
                ))}
              {preview &&
                board.turns
                  .filter((t) => preview.moved.has(t.id) && board.plan.stands[t.id]?.standId === s.id)
                  .map((t) => (
                    <div
                      key={`ghost-${t.id}`}
                      className="pointer-events-none absolute top-1.5 bottom-1.5 rounded-md border border-dashed border-ink-3/60"
                      style={{ left: x(t.start), width: Math.max(x(t.end) - x(t.start), 18) }}
                    />
                  ))}
              {(edgeByStand.get(s.id) ?? []).map((t) => (
                <div
                  key={`edge-${t.id}`}
                  className="pointer-events-none absolute top-1 bottom-1 overflow-hidden rounded-md border border-dashed border-line/70 bg-panel-2/40 px-1.5 py-0.5 text-[11px] text-ink-3 opacity-60"
                  style={{ left: scale.x(t.start), width: Math.max(8, scale.x(t.end) - scale.x(t.start)) }}
                  title={`${label(t)} · ${t.start < dayWindow(board.date, board.airport.timezone).from ? "yesterday" : "tomorrow"}, planned on that day`}
                >
                  <span className="truncate">{label(t)}</span>
                </div>
              ))}
              {(byStand.get(s.id) ?? []).map(turnBar)}
              <NowLine scale={scale} now={now} />
            </div>
          </div>
        ))}

        {/*
          C1: the day's cancelled flights, so the team sees what is not operating instead of
          a bar quietly vanishing. Not a stand: nothing can be dropped here, nothing on it
          holds a stand, counter or belt, and none of it counts as a conflict. Each flight
          sits at its scheduled time, stacked when several were due together.
        */}
        <div className="flex border-b border-line bg-panel-2/40" style={{ minHeight: ROW_H }}>
          <div className="sticky left-0 z-40 flex shrink-0 items-center border-r border-line bg-panel px-3" style={{ width: LABEL_W }}>
            <div>
              <div className="text-[13px] font-semibold">C1 · Cancelled</div>
              <div className="text-[10px] text-ink-3">{cancelledLanes.length ? `${cancelledLanes.length} flight${cancelledLanes.length > 1 ? "s" : ""} not operating` : "No cancellations"}</div>
            </div>
          </div>
          <div className="relative" style={{ width, height: Math.max(1, ...cancelledLanes.map((c) => c.lane + 1)) * (ROW_H - 12) + 12 }}>
            <GridLines scale={scale} />
            {cancelledLanes.map((c) => {
              const left = x(Math.max(c.from, from));
              const w = Math.max(x(Math.min(c.to, to)) - left, 18);
              return (
                <div
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  onMouseEnter={(e) => !drag && props.onHover(c.id, e.clientX, e.clientY)}
                  onMouseMove={(e) => !drag && props.onHover(c.id, e.clientX, e.clientY)}
                  onMouseLeave={() => props.onHover(null, 0, 0)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    props.onHover(null, 0, 0);
                    props.onContext(c.id, e.clientX, e.clientY);
                  }}
                  className="hatch absolute flex cursor-default items-center gap-1 overflow-hidden rounded-md border border-dashed border-ink-3/60 bg-panel-2 px-1.5 text-[12px] leading-tight text-ink-3 select-none"
                  style={{ left, width: w, top: 6 + c.lane * (ROW_H - 12), height: ROW_H - 16, borderLeft: `4px solid ${airlineColor(c.airline, config)}` }}
                  title={`${c.leg.flightNumber} cancelled · due ${hhmm(c.leg.scheduled, tz)}`}
                >
                  <span className="opacity-75">{c.leg.direction === "ARR" ? "↓" : "↑"}</span>
                  <span className="truncate font-semibold line-through">{c.leg.flightNumber}</span>
                  {w > 70 && <span className="tabular truncate text-[10.5px]">{hhmm(c.leg.scheduled, tz)}</span>}
                  {c.bags && <Luggage className="size-3 shrink-0" aria-label="Bags back to a reclaim belt" />}
                </div>
              );
            })}
            <NowLine scale={scale} now={now} />
          </div>
        </div>

        {(unallocated.length > 0 || drag) && (
          <div className={clsx("flex border-b border-line bg-bad/5", rowTint(null))} style={{ minHeight: ROW_H }} data-stand="">
            <div className="sticky left-0 z-40 flex shrink-0 items-center border-r border-line bg-panel px-3" style={{ width: LABEL_W }} data-stand="">
              <div>
                <div className="text-[13px] font-semibold text-bad">Unallocated</div>
                <div className="text-[10px] text-ink-3">{unallocated.length} turns</div>
              </div>
            </div>
            <div className="relative" style={{ width, height: ROW_H }} data-stand="">
              <GridLines scale={scale} />
              {unallocated.map(turnBar)}
              <NowLine scale={scale} now={now} />
            </div>
          </div>
        )}
      </div>

      {drag?.check && (
        <div
          className={clsx(
            "pointer-events-none fixed z-50 max-w-72 rounded-md px-2 py-1 text-[11px] font-medium shadow-lg",
            drag.check.level === "ok" ? "bg-ok text-white" : drag.check.level === "soft" ? "bg-warn text-white" : "bg-bad text-white",
          )}
          style={{ left: drag.x + 14, top: drag.y + 12 }}
        >
          {drag.standId ?? "Unallocated"} — {drag.check.reason}
        </div>
      )}
      {resize && resize.until !== resize.end && (
        <div
          className={clsx("pointer-events-none fixed z-50 max-w-72 rounded-md px-2 py-1 text-[11px] font-medium text-white shadow-lg", stretchClash ? "bg-bad" : "bg-warn")}
          style={{ left: resize.x + 14, top: resize.y + 12 }}
        >
          {resize.until <= resize.usual
            ? `Back to ${hhmm(resize.usual, tz)}: extension removed`
            : `On stand until ${hhmm(resize.until, tz)} (+${Math.round((resize.until - resize.usual) / 60_000)} min)${stretchClash ? `, runs into ${label(stretchClash)}` : ""}`}
        </div>
      )}
    </div>
      {props.onDay && (
        <DayButtons
          date={board.date}
          tz={tz}
          atEdge={atEdge}
          onDay={props.onDay}
        />
      )}
    </div>
  );
}

/**
 * The board runs on into the days either side. The day shows six hours past each midnight;
 * these open the next or previous day where the scroll left off, so an evening can be
 * followed into the morning without the top bar. Brighter once scrolled to that end.
 */
function DayButtons({ date, tz, atEdge, onDay }: { date: string; tz: string; atEdge: { start: boolean; end: boolean }; onDay: (date: string, focusAt: number) => void }) {
  const prev = addDays(date, -1);
  const next = addDays(date, 1);
  const day = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-CA", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
  const btn = (on: boolean) =>
    clsx(
      "absolute top-1/2 z-50 flex -translate-y-1/2 items-center gap-1 rounded-full border px-2 py-2 text-[12px] font-semibold shadow-lg transition-opacity",
      on ? "border-info bg-info text-white opacity-100" : "border-line bg-panel text-ink-2 opacity-70 hover:opacity-100",
    );
  return (
    <>
      <button
        className={btn(atEdge.start)}
        style={{ left: LABEL_W + 8 }}
        title={`Previous day: ${day(prev)}`}
        aria-label={`Previous day, ${day(prev)}`}
        onClick={() => onDay(prev, dayWindow(prev, tz).to - 3 * 3_600_000)}
      >
        <ChevronLeft className="size-4" />
        {atEdge.start && <span>{day(prev)}</span>}
      </button>
      <button
        className={clsx(btn(atEdge.end), "right-3")}
        title={`Next day: ${day(next)}`}
        aria-label={`Next day, ${day(next)}`}
        onClick={() => onDay(next, dayWindow(next, tz).from)}
      >
        {atEdge.end && <span>{day(next)}</span>}
        <ChevronRight className="size-4" />
      </button>
    </>
  );
}
